import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getFeaturedWorks } from "@/lib/works";
import { TYPE_LABELS } from "@/types/work";

const SLIDES = getFeaturedWorks(5);

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const timerRef = useRef<number | null>(null);
  const touchStartX = useRef<number | null>(null);

  const goTo = useCallback((index: number) => {
    setActive((index + SLIDES.length) % SLIDES.length);
  }, []);

  useEffect(() => {
    timerRef.current = window.setInterval(() => {
      setActive((prev) => (prev + 1) % SLIDES.length);
    }, 6000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, []);

  const pause = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
  };
  const resume = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      setActive((prev) => (prev + 1) % SLIDES.length);
    }, 6000);
  };

  return (
    <section
      className="relative h-[520px] w-full overflow-hidden bg-foreground-950 md:h-[680px]"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0].clientX;
      }}
      onTouchEnd={(event) => {
        if (touchStartX.current === null) return;
        const delta = event.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 50) goTo(active + (delta < 0 ? 1 : -1));
        touchStartX.current = null;
      }}
    >
      {SLIDES.map((work, index) => (
        <div
          key={work.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={work.heroImage}
            alt={`${work.title} 焦点剧照`}
            title={`${work.title} 剧情介绍`}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-foreground-950/30" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full max-w-content flex-col justify-end px-4 pb-20 md:px-6 md:pb-24">
        <div className="max-w-2xl">
          {SLIDES.map((work, index) => (
            <div
              key={work.id}
              className={`transition-all duration-500 ${
                index === active
                  ? "block animate-fade-up"
                  : "pointer-events-none hidden"
              }`}
            >
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-background-50 dark:text-foreground-950">
                  {TYPE_LABELS[work.type]}
                </span>
                <span className="rounded-md bg-background-50/15 px-2.5 py-1 text-xs font-medium text-background-50 backdrop-blur-sm">
                  {work.region} · {work.year}
                </span>
                <span className="flex items-center gap-1 rounded-md bg-accent-500/95 px-2.5 py-1 text-xs font-semibold text-accent-950">
                  <i className="ri-star-fill"></i>
                  {work.rating.toFixed(1)}
                </span>
              </div>
              <h1 className="font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
                {work.title}
              </h1>
              <p className="clamp-2 mt-4 text-sm leading-relaxed text-background-100/85 md:text-base">
                {work.shortIntro}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${work.id}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer dark:text-foreground-950"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  查看剧情详情
                </Link>
                <Link
                  to={`/category/${work.type}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/15 cursor-pointer"
                >
                  <i className="ri-movie-line text-lg"></i>
                  浏览同类作品
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex items-center gap-3">
          {SLIDES.map((work, index) => (
            <button
              key={work.id}
              type="button"
              aria-label={`切换到第 ${index + 1} 张`}
              onClick={() => goTo(index)}
              className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                index === active
                  ? "w-8 bg-primary-500"
                  : "w-4 bg-background-50/40 hover:bg-background-50/70"
              }`}
            />
          ))}
        </div>
      </div>

      <div className="absolute bottom-20 right-4 z-10 hidden items-center gap-2 md:bottom-24 md:right-6 md:flex">
        <button
          type="button"
          aria-label="上一张"
          onClick={() => goTo(active - 1)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/30 bg-foreground-950/30 text-background-50 backdrop-blur-sm transition-colors hover:bg-primary-500 hover:text-background-50 cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => goTo(active + 1)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/30 bg-foreground-950/30 text-background-50 backdrop-blur-sm transition-colors hover:bg-primary-500 hover:text-background-50 cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>
    </section>
  );
}